import React, { useEffect, useState, lazy, memo, createElement } from 'react';
import { ChevronDown, Check, ShieldCheck, Lock, CreditCard, HelpCircle, AlertTriangle, BookOpen, Heart, Users, Gift, Sparkles, Target, Brain, Clock, Rainbow, Star, MessageCircle, Smile } from 'lucide-react';
import { CountdownTimer } from './components/CountdownTimer';
import { PurchaseNotification } from './components/PurchaseNotification';
import { CTAButton } from './components/CTAButton';
import { TestimonialCard } from './components/TestimonialCard';
const CHECKOUT_URL = 'https://pay.hotmart.com/W103408326L?off=us1hzf36&checkoutMode=10';
const FAQ_DATA = [{
  q: '¿Cómo recibo los libros después de comprar?',
  a: 'Inmediatamente después de tu compra, recibirás un email con el acceso para descargar todos los libros en formato PDF.'
}, {
  q: '¿Puedo imprimir los cuadernillos?',
  a: 'Sí, todos los libros están diseñados para ser impresos fácilmente en cualquier impresora.'
}, {
  q: '¿Puedo verlos desde mi celular o necesito computadora?',
  a: 'Podés verlos desde cualquier dispositivo: celular, tablet o computadora. Son archivos PDF compatibles con todos.'
}, {
  q: '¿Puedo regalar el pack a otra persona?',
  a: 'Por supuesto, es un regalo perfecto para cualquier familia. Podés compartir los archivos con quien quieras.'
}, {
  q: '¿Cuánto tarda en llegar el pack después de pagar?',
  a: 'El acceso es inmediato. Apenas completás el pago, recibís el email con los enlaces de descarga.'
}, {
  q: '¿Qué incluye exactamente el Pack 100 Citas Familiares?',
  a: '4 libros digitales completos, más 7 bonos exclusivos con actividades, juegos y herramientas para fortalecer la conexión familiar.'
}, {
  q: '¿Es una compra segura?',
  a: 'Sí, la compra se realiza a través de Hotmart, una plataforma reconocida mundialmente. Además, tenés 7 días de garantía total.'
}] as const;
const BONUS_DATA = [{
  title: '30 cosas que quiero que mi hija nunca olvide aunque yo no esté',
  desc: '30 frases y mensajes que le recordarán cuánto la amás, incluso cuando no estés cerca.',
  img: 'https://cdn.magicpatterns.com/uploads/wJDf6dAfCTYYDQfEzH9uWq/BONO_3.png',
  emoji: '💌'
}, {
  title: '52 Planes Mágicos 🎉',
  desc: 'Actividades simples, divertidas y económicas para disfrutar en familia todo el año.',
  img: 'https://cdn.magicpatterns.com/uploads/2R4xT7m85n4thrbxN8Vkxn/BONO_2.png',
  emoji: '🎉'
}, {
  title: 'Juegos del Corazón 🎲',
  desc: 'Dinámicas secretas para descubrir lo que tu hija realmente siente y piensa mientras se divierten juntos.',
  img: 'https://cdn.magicpatterns.com/uploads/vd6PYYow3c9ZtvWdGcWJS1/BONO_1.png',
  emoji: '🎲'
}, {
  title: 'Abuelita, Tu Historia 👵',
  desc: 'Un hermoso libro para que tu abuela comparta sus recuerdos, anécdotas y enseñanzas de vida.',
  img: 'https://cdn.magicpatterns.com/uploads/akhJP4pXk6V5aVW2aFHBEd/BONO_4.png',
  emoji: '👵'
}, {
  title: 'Abuelito, Tu Historia 👴',
  desc: 'Un espacio para que el abuelo cuente sus vivencias, sabiduría y recuerdos que quedarán para siempre.',
  img: 'https://cdn.magicpatterns.com/uploads/3FpY5CTan4q6KXEDKXNSMa/BONO_5.png',
  emoji: '👴'
}, {
  title: 'Mamá, Tu Historia 👩',
  desc: 'Un recuerdo invaluable donde mamá puede dejar plasmadas sus experiencias, consejos y momentos más especiales.',
  img: 'https://cdn.magicpatterns.com/uploads/92HR4yVGGmq5KmZQpwtpGN/BONO_6.png',
  emoji: '👩'
}, {
  title: 'Papá, Tu Historia 👨',
  desc: 'Un libro para que papá comparta sus recuerdos, enseñanzas y los momentos que marcaron su vida.',
  img: 'https://cdn.magicpatterns.com/uploads/bkZRYgYEwHZbMbGTMPbGrC/BONO_7.png',
  emoji: '👨'
}] as const;
const PAIN_POINTS = [{
  emoji: '🕒',
  text: 'Pasan los días y sentís que apenas compartís momentos reales con tus hijos.'
}, {
  emoji: '📱',
  text: 'Todo se vuelve pantallas, trabajo y cansancio… y las charlas se hacen cada vez más cortas.'
}, {
  emoji: '💤',
  text: 'Aunque amás a tu familia, muchas noches terminás con la sensación de que no estás aprovechando el tiempo juntos.'
}, {
  emoji: '⚠️',
  text: 'Si no cambiás algo ahora, los días seguirán pasando sin esos recuerdos que tanto soñás crear.'
}] as const;
const PRODUCT_FEATURES = [{
  emoji: '📚',
  text: '4 libros digitales llenos de conexión — cada uno pensado para fortalecer la relación entre padres e hijos.'
}, {
  emoji: '🎉',
  text: 'Adiós al aburrimiento, con actividades divertidas, simples y económicas para disfrutar en cualquier momento.'
}, {
  emoji: '🗝️',
  text: 'Juegos secretos que te ayudan a descubrir lo que tus hijos sienten, piensan y sueñan, mientras se divierten juntos.'
}, {
  emoji: '💌',
  text: '30 frases y actividades especiales para que tus hijos nunca olviden cuánto los amás y lo importante que son para vos.'
}, {
  emoji: '🎁',
  text: 'Bonos exclusivos inspirados en el universo de 100 Citas, con ideas adicionales y retos semanales para mantener viva la conexión familiar.'
}] as const;
const RECEIVE_ITEMS = [{
  emoji: '📚',
  text: '4 libros digitales completos, diseñados para fortalecer la conexión entre padres e hijos.'
}, {
  emoji: '🎉',
  text: 'Más de 500 ideas para compartir momentos únicos, divertidos y llenos de emoción.'
}, {
  emoji: '🗝️',
  text: 'Juegos secretos que revelan lo que sienten tus hijos y mejoran la comunicación familiar.'
}, {
  emoji: '💌',
  text: 'Frases y actividades con propósito, para que tus hijos nunca olviden cuánto los amás.'
}, {
  emoji: '🎁',
  text: 'Bonos exclusivos con retos semanales e ideas extras que mantienen viva la magia del vínculo familiar.'
}] as const;
const FAMILY_ACHIEVE = [{
  emoji: '💬',
  text: 'Charlar más y conectar de verdad, alejándose de las pantallas y volviendo a lo esencial.'
}, {
  emoji: '😄',
  text: 'Crear recuerdos inolvidables sin gastar mucho dinero, con actividades simples y efectivas.'
}, {
  emoji: '💖',
  text: 'Fortalecer la confianza y la comunicación, compartiendo momentos sinceros y espontáneos.'
}, {
  emoji: '⏰',
  text: 'Aprovechar el tiempo juntos, sin necesidad de planificar ni buscar ideas.'
}, {
  emoji: '🌈',
  text: 'Recuperar la alegría en los pequeños momentos del día a día.'
}] as const;
const PARENT_ACHIEVE = [{
  emoji: '✨',
  text: 'Dejar atrás la rutina y vivir experiencias reales que unan a tu familia.'
}, {
  emoji: '🧠',
  text: 'Descubrir nuevas formas de conectar, entendiendo más a tus hijos.'
}, {
  emoji: '💞',
  text: 'Sentirte presente y valorado, sabiendo que estás creando recuerdos para toda la vida.'
}, {
  emoji: '🎯',
  text: 'Ahorrar tiempo y energía, con ideas listas para usar sin planificar.'
}, {
  emoji: '🌟',
  text: 'Convertirte en el corazón de tu familia, inspirando amor, unión y diversión.'
}] as const;
const BonusCard = memo(({
  bonus,
  index
}: {
  bonus: (typeof BONUS_DATA)[number];
  index: number;
}) => <div className="bg-gray-800 rounded-2xl p-6 md:p-8 border border-gray-700 flex flex-col md:flex-row gap-8 items-center" data-id="element-0">
      {bonus.img ? <div className="w-full md:w-1/3 flex-shrink-0" data-id="element-1">
          <img src={bonus.img} alt={bonus.title} className="w-full rounded-lg shadow-2xl" loading="lazy" width="400" height="600" decoding="async" data-id="element-2" />
        </div> : <div className="w-full md:w-1/3 flex-shrink-0 bg-gray-700 h-64 rounded-lg flex items-center justify-center text-gray-500" data-id="element-3">
          <Gift className="w-16 h-16 opacity-50" data-id="element-4" />
        </div>}
      <div className="flex-1 text-center md:text-left" data-id="element-5">
        <div className="inline-block bg-green-600 text-white text-xs font-bold px-2 py-1 rounded mb-3" data-id="element-6">
          GRATIS HOY CON TU COMPRA
        </div>
        <h3 className="text-2xl font-bold text-yellow-400 mb-3" data-id="element-7">
          {bonus.title}
        </h3>
        <p className="text-lg text-gray-300" data-id="element-8">{bonus.desc}</p>
      </div>
    </div>);
const FAQItem = memo(({
  faq,
  index,
  isOpen,
  onToggle
}: any) => <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-9">
    <button onClick={() => onToggle(index)} className="w-full flex items-center justify-between p-5 text-left font-bold text-gray-900 hover:bg-gray-50 transition-colors" aria-expanded={isOpen} data-id="element-10">
      {faq.q}
      <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-11" />
    </button>
    {isOpen && <div className="p-5 pt-0 text-gray-600 border-t border-gray-100 bg-gray-50" data-id="element-12">
        {faq.a}
      </div>}
  </div>);
const CheckListItem = memo(({
  emoji,
  text
}: {
  emoji: string;
  text: string;
}) => <li className="flex items-start gap-3" data-id="element-13">
      <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-14">{emoji}</span>
      <span className="text-gray-700" data-id="element-15">{text}</span>
    </li>);
export function App() {
  // Meta Pixel Integration - defer to not block rendering
  useEffect(() => {
    const loadPixel = () => {
      const script = document.createElement('script');
      script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '885866990629991');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(script);
      const noscript = document.createElement('noscript');
      noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=885866990629991&ev=PageView&noscript=1" />`;
      document.body.appendChild(noscript);
    };
    if (document.readyState === 'complete') {
      loadPixel();
    } else {
      window.addEventListener('load', loadPixel);
      return () => window.removeEventListener('load', loadPixel);
    }
  }, []);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  return <div className="font-sans text-gray-900 bg-white pt-12" data-id="element-16">
      <CountdownTimer data-id="element-17" />
      <PurchaseNotification data-id="element-18" />

      {/* HERO SECTION */}
      <header className="bg-white pb-12 pt-8 px-4 text-center border-b border-gray-200" data-id="element-19">
        <div className="max-w-4xl mx-auto" data-id="element-20">
          <h1 className="text-3xl md:text-5xl font-black mb-6 leading-tight tracking-tight text-gray-900" data-id="element-21">
            Menos pantallas, más abrazos:{' '}
            <span className="text-red-600" data-id="element-22">
              descubrí el poder del Pack 100 Citas
            </span>
          </h1>

          <div className="my-8 flex justify-center" data-id="element-23">
            <img src="https://cdn.magicpatterns.com/uploads/q68Va7f1235i4y2MMkjDDD/MOCAP_100_CITAS.png" alt="Pack 100 Citas Completo" className="w-full max-w-2xl drop-shadow-2xl" loading="eager" fetchPriority="high" width="800" height="600" decoding="async" data-id="element-24" />
          </div>

          <div className="bg-yellow-100 border-l-8 border-yellow-400 p-6 mb-8 text-left max-w-3xl mx-auto rounded-r-lg shadow-sm" data-id="element-25">
            <p className="text-lg md:text-xl text-gray-800 leading-relaxed" data-id="element-26">
              Descubre el Pack 100 Citas 👨‍👩‍👧‍👦 y vive experiencias únicas con tus
              hijos y toda la familia. Cada cita 💫 es un momento especial que
              fortalece los lazos, despierta sonrisas y crea recuerdos para
              siempre ❤️.
            </p>
          </div>

          <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="group relative inline-flex flex-col items-center justify-center w-full md:w-auto min-w-[300px] bg-green-500 hover:bg-green-600 text-white font-black py-4 px-8 rounded-lg shadow-[0_6px_0_0_rgb(21,128,61)] hover:shadow-[0_3px_0_0_rgb(21,128,61)] hover:translate-y-[3px] transition-all duration-150 transform text-center uppercase tracking-wide" data-id="element-27">
            <span className="flex items-center gap-2 text-xl md:text-2xl drop-shadow-md" data-id="element-28">
              👉 SÍ, QUIERO LA OFERTA AHORA
            </span>
            <span className="text-xs md:text-sm font-normal normal-case opacity-90 mt-1" data-id="element-29">
              Descarga Digital Inmediata
            </span>
          </a>

          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-600 font-medium" data-id="element-30">
            <ShieldCheck className="w-5 h-5 text-green-600" data-id="element-31" />
            Garantía de satisfacción de 7 días
          </div>
        </div>
      </header>

      {/* PROBLEM SECTION */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-32">
        <div className="max-w-3xl mx-auto" data-id="element-33">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" data-id="element-34">
            💔 Mientras tratás de conectar con tu familia… el tiempo se te
            escapa
          </h2>

          <div className="space-y-5" data-id="element-35">
            {PAIN_POINTS.map((item, i) => <div key={i} className="flex items-start gap-4 bg-white p-5 rounded-xl shadow-sm border-l-4 border-red-500" data-id="element-36">
                <span className="text-2xl flex-shrink-0" data-id="element-37">{item.emoji}</span>
                <p className="text-lg text-gray-700 leading-relaxed" data-id="element-38">
                  {item.text}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* SOLUTION BRIDGE */}
      <section className="py-16 px-4 bg-white" data-id="element-39">
        <div className="max-w-3xl mx-auto" data-id="element-40">
          <div className="text-center mb-10" data-id="element-41">
            <h2 className="text-3xl md:text-4xl font-black mb-3 text-gray-900" data-id="element-42">
              No es falta de amor ni de ganas ❤️
            </h2>
            <p className="text-xl md:text-2xl font-bold text-gray-600" data-id="element-43">
              Es que no tenés el método correcto para reconectar.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 space-y-5 text-lg text-gray-700 leading-relaxed" data-id="element-44">
            <p data-id="element-45">
              No es falta de ganas, es que la rutina te roba el tiempo. Sin una
              guía que te motive a crear recuerdos reales, 💭 seguís dejando los
              momentos importantes "para después"… y después nunca es hoy.
            </p>
            <p data-id="element-46">
              🔑 Lo que necesitás es una herramienta que te devuelva el tiempo,
              te inspire y te ayude a crear recuerdos sin esfuerzo.
            </p>
            <p data-id="element-47">
              📔 Con las "100 Citas Familiares" vas a tener ideas listas para
              cada día, sin planificar nada.
            </p>
            <p className="font-semibold text-gray-900" data-id="element-48">
              ✨ Solo elegís una cita, la compartís y disfrutás. Más conexión,
              más risas, más momentos inolvidables 💞
            </p>
          </div>
        </div>
      </section>

      {/* PRODUCT INTRO */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-49">
        <div className="max-w-4xl mx-auto" data-id="element-50">
          <div className="text-center mb-12" data-id="element-51">
            <div className="inline-block bg-pink-100 text-pink-800 px-4 py-1 rounded-full font-bold mb-4 border border-pink-200" data-id="element-52">
              💖 La solución
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900" data-id="element-53">
              💖 Por eso creamos el Pack 100 Citas Familiares
            </h2>
          </div>

          <div className="space-y-4" data-id="element-54">
            {PRODUCT_FEATURES.map((item, i) => <div key={i} className="flex items-start gap-4 bg-white p-5 rounded-xl shadow-md border-l-4 border-blue-500" data-id="element-55">
                <span className="text-2xl flex-shrink-0" data-id="element-56">{item.emoji}</span>
                <p className="text-lg text-gray-700" data-id="element-57">{item.text}</p>
              </div>)}
          </div>

          <div className="mt-10 text-center" data-id="element-58">
            <CTAButton text="QUIERO EL PACK 100 CITAS AHORA" href={CHECKOUT_URL} data-id="element-59" />
          </div>
        </div>
      </section>

      {/* WHY UNIQUE */}
      <section className="py-16 px-4 bg-white" data-id="element-60">
        <div className="max-w-4xl mx-auto" data-id="element-61">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-12 text-gray-900" data-id="element-62">
            💞 Por qué el Pack 100 Citas Familiares es único y especial
          </h2>

          {/* What you receive */}
          <div className="mb-12" data-id="element-63">
            <h3 className="text-2xl font-black mb-6 flex items-center gap-2" data-id="element-64">
              <Check className="w-7 h-7 text-green-600" data-id="element-65" />
              ¿Qué vas a recibir?
            </h3>
            <div className="space-y-3" data-id="element-66">
              {RECEIVE_ITEMS.map((item, i) => <div key={i} className="flex items-start gap-3 bg-green-50 p-4 rounded-lg" data-id="element-67">
                  <span className="text-xl flex-shrink-0" data-id="element-68">{item.emoji}</span>
                  <p className="text-gray-700" data-id="element-69">{item.text}</p>
                </div>)}
            </div>
          </div>

          {/* What you achieve with family */}
          <div className="mb-12" data-id="element-70">
            <h3 className="text-2xl font-black mb-6 flex items-center gap-2" data-id="element-71">
              <Check className="w-7 h-7 text-green-600" data-id="element-72" />
              ¿Qué vas a lograr con tu familia?
            </h3>
            <div className="space-y-3" data-id="element-73">
              {FAMILY_ACHIEVE.map((item, i) => <div key={i} className="flex items-start gap-3 bg-blue-50 p-4 rounded-lg" data-id="element-74">
                  <span className="text-xl flex-shrink-0" data-id="element-75">{item.emoji}</span>
                  <p className="text-gray-700" data-id="element-76">{item.text}</p>
                </div>)}
            </div>
          </div>

          {/* What you achieve as parent */}
          <div className="mb-12" data-id="element-77">
            <h3 className="text-2xl font-black mb-6 flex items-center gap-2" data-id="element-78">
              <Check className="w-7 h-7 text-green-600" data-id="element-79" />
              ¿Qué vas a lograr vos como mamá o papá?
            </h3>
            <div className="space-y-3" data-id="element-80">
              {PARENT_ACHIEVE.map((item, i) => <div key={i} className="flex items-start gap-3 bg-purple-50 p-4 rounded-lg" data-id="element-81">
                  <span className="text-xl flex-shrink-0" data-id="element-82">{item.emoji}</span>
                  <p className="text-gray-700" data-id="element-83">{item.text}</p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-16 px-4 bg-gray-900 text-white" data-id="element-84">
        <div className="max-w-5xl mx-auto" data-id="element-85">
          <div className="text-center mb-16" data-id="element-86">
            <span className="bg-yellow-400 text-black font-black px-4 py-1 rounded uppercase tracking-wider text-sm" data-id="element-87">
              ¡PERO ESO NO ES TODO!
            </span>
            <h2 className="text-3xl md:text-4xl font-black mt-4 mb-4 text-white" data-id="element-88">
              🎁 Accediendo hoy te damos este REGALO EXCLUSIVO
            </h2>
          </div>

          <div className="space-y-12" data-id="element-89">
            {BONUS_DATA.map((bonus, index) => <BonusCard key={index} bonus={bonus} index={index} data-id="element-90" />)}
          </div>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section id="pricing" className="py-16 px-4 bg-white relative overflow-hidden" data-id="element-91">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-50" data-id="element-92"></div>
        <div className="max-w-3xl mx-auto relative z-10" data-id="element-93">
          <div className="bg-white rounded-3xl shadow-2xl border-4 border-yellow-400 overflow-hidden" data-id="element-94">
            <div className="bg-red-600 text-white text-center py-4 px-4 animate-pulse" data-id="element-95">
              <p className="font-bold text-lg uppercase tracking-wider" data-id="element-96">
                🎉 OFERTA POR TIEMPO LIMITADO
              </p>
            </div>

            <div className="p-8 md:p-12 text-center" data-id="element-97">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-98">
                ✅ Estás a un paso de acceder al Pack 100 Citas Familiares, un
                conjunto único de 5 libros digitales diseñados para fortalecer
                la conexión con tus hijos y toda tu familia.
              </p>

              <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left max-w-lg mx-auto border border-gray-200" data-id="element-99">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-100">
                  <Gift className="w-5 h-5 text-purple-600" data-id="element-101" />
                  🎁 Te llevás todo esto dentro del Pack 100 Citas Familiares
                </h3>
                <ul className="space-y-3 mb-4" data-id="element-102">
                  <li className="flex items-start gap-3" data-id="element-103">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-104" />
                    <span className="text-gray-700 font-bold" data-id="element-105">
                      4 Libros Digitales llenos de actividades, juegos y
                      momentos únicos:
                    </span>
                  </li>
                  <li className="flex items-start gap-3 ml-6" data-id="element-106">
                    <span className="text-gray-600" data-id="element-107">
                      📘 100 Citas con mi Hija (versión mamá e hija)
                    </span>
                  </li>
                  <li className="flex items-start gap-3 ml-6" data-id="element-108">
                    <span className="text-gray-600" data-id="element-109">
                      📗 100 Citas con mi Hijo (versión mamá e hijo)
                    </span>
                  </li>
                  <li className="flex items-start gap-3 ml-6" data-id="element-110">
                    <span className="text-gray-600" data-id="element-111">
                      📙 100 Citas con mi Hija (versión papá e hija)
                    </span>
                  </li>
                  <li className="flex items-start gap-3 ml-6" data-id="element-112">
                    <span className="text-gray-600" data-id="element-113">
                      📕 100 Citas con mi Hijo (versión papá e hijo)
                    </span>
                  </li>
                </ul>

                <p className="font-bold text-gray-900 mb-3" data-id="element-114">
                  ✨ Cada libro incluye:
                </p>
                <ul className="space-y-2 mb-4 ml-2" data-id="element-115">
                  <li className="flex items-start gap-2" data-id="element-116">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" data-id="element-117" />
                    <span className="text-gray-600 text-sm" data-id="element-118">
                      Actividades y desafíos familiares simples, económicos y
                      divertidos
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-119">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" data-id="element-120" />
                    <span className="text-gray-600 text-sm" data-id="element-121">
                      Momentos guiados para fortalecer la conexión emocional
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-122">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" data-id="element-123" />
                    <span className="text-gray-600 text-sm" data-id="element-124">
                      Ideas para desconectarse de las pantallas y reconectarse
                      entre ustedes
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-125">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" data-id="element-126" />
                    <span className="text-gray-600 text-sm" data-id="element-127">
                      Espacios para escribir, reflexionar y guardar recuerdos
                    </span>
                  </li>
                </ul>

                <p className="font-bold text-gray-900 mb-3" data-id="element-128">
                  🎁 Además, te llevás 7 bonos exclusivos:
                </p>
                <ul className="space-y-2 ml-2" data-id="element-129">
                  <li className="text-gray-600 text-sm" data-id="element-130">
                    💌 Recuerdos para mi hija – 30 mensajes y reflexiones que
                    ella recordará toda la vida
                  </li>
                  <li className="text-gray-600 text-sm" data-id="element-131">
                    🎲 Adiós al aburrimiento – 52 planes económicos y
                    entretenidos para disfrutar todo el año
                  </li>
                  <li className="text-gray-600 text-sm" data-id="element-132">
                    🔑 Juegos secretos – Actividades que revelan lo que tus
                    hijos realmente piensan y sienten
                  </li>
                  <li className="text-gray-600 text-sm" data-id="element-133">
                    👵 Abuelita, cuéntame tu historia – Un viaje emocional por
                    sus recuerdos
                  </li>
                  <li className="text-gray-600 text-sm" data-id="element-134">
                    👴 Abuelito, cuéntame tu historia – Un legado familiar lleno
                    de anécdotas
                  </li>
                  <li className="text-gray-600 text-sm" data-id="element-135">
                    👩‍👧 Mamá, cuéntame tu historia – Un espacio íntimo para
                    compartir experiencias
                  </li>
                  <li className="text-gray-600 text-sm" data-id="element-136">
                    👨‍👦 Papá, cuéntame tu historia – Momentos de conexión
                    profunda entre padre e hijo
                  </li>
                </ul>
              </div>

              <div className="mb-8" data-id="element-137">
                <p className="text-gray-500 line-through text-xl mb-1" data-id="element-138">
                  ¡Antes $25.00 USD!
                </p>
                <p className="text-sm font-bold text-red-500 mb-2" data-id="element-139">
                  SOLO POR HOY
                </p>
                <div className="flex items-center justify-center gap-2" data-id="element-140">
                  <span className="text-6xl font-black text-green-600 tracking-tighter" data-id="element-141">
                    USD $6,99
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-2" data-id="element-142">
                  ¡Pagalo en tu Moneda Local!
                </p>
              </div>

              <CTAButton text="SÍ, QUIERO LA OFERTA AHORA" className="w-full md:w-full" href={CHECKOUT_URL} data-id="element-143" />

              <div className="mt-6 flex justify-center gap-4 opacity-60 grayscale hover:grayscale-0 transition-all" data-id="element-144">
                <CreditCard className="w-8 h-8" data-id="element-145" />
                <Lock className="w-8 h-8" data-id="element-146" />
                <ShieldCheck className="w-8 h-8" data-id="element-147" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 px-4 bg-white" data-id="element-148">
        <div className="max-w-6xl mx-auto" data-id="element-149">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-12" data-id="element-150">
            💬 Lo que las familias dicen del Pack 100 Citas
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-151">
            <TestimonialCard quote="Desde que tenemos el Pack 100 Citas, cada fin de semana se siente diferente. Ya no estamos todo el día con el celular. Hacemos los juegos, nos reímos, y siento que volví a conectar con mis hijos de verdad. Es el mejor regalo que nos hicimos como familia 💖." author="Camila Godoy" role="mamá" data-id="element-152" />
            <TestimonialCard quote="Con mi hijo siempre costaba encontrar tiempo para charlar. 100 Citas de Papá e Hijo nos dio la excusa perfecta para hacerlo. No solo hablamos, nos entendimos más. Es algo que todo padre debería vivir." author="Martín Gutierrez" role="papá" data-id="element-153" />
            <TestimonialCard quote="Compré el pack pensando que era algo simple, pero me sorprendió lo profundo que es. Cada cita tiene un sentido, una emoción. Mi hija espera con ilusión el momento de abrir una nueva página. ¡Nos unió más de lo que imaginé!" author="Laura Garcia" role="mamá" data-id="element-154" />
            <TestimonialCard quote="Mi hijo siempre estaba con la tablet, y sentía que cada vez hablábamos menos. Desde que empezamos con 100 Citas con mi Hijo, tenemos nuestro 'momento especial'. Hacemos los desafíos, hablamos de todo y nos reímos muchísimo. No es solo un libro, es tiempo de calidad que nos hacía falta ❤️." author="Romina D." role="mamá" data-id="element-155" />
          </div>
        </div>
      </section>

      {/* GUARANTEE SECTION */}
      <section className="py-12 px-4 bg-gray-50 border-y border-gray-200" data-id="element-156">
        <div className="max-w-3xl mx-auto flex flex-col md:flex-row items-center gap-8" data-id="element-157">
          <div className="flex-shrink-0" data-id="element-158">
            <div className="w-32 h-32 bg-yellow-400 rounded-full flex items-center justify-center border-4 border-yellow-200 shadow-lg rotate-12" data-id="element-159">
              <ShieldCheck className="w-16 h-16 text-white" data-id="element-160" />
            </div>
          </div>
          <div className="text-center md:text-left" data-id="element-161">
            <h3 className="text-2xl font-black mb-2" data-id="element-162">
              🔒 Garantía sin vueltas
            </h3>
            <p className="text-lg text-gray-700 mb-4" data-id="element-163">
              Estamos tan seguros de que este Programa te va a encantar, que si
              no es así, te devolvemos el 100% de tu dinero. Tenés 7 días para
              probarlo sin riesgo. Si no te convence, nos escribís y te hacemos
              el reembolso. Así de simple.
            </p>
            <div className="flex flex-wrap justify-center md:justify-start gap-4 font-bold text-gray-900" data-id="element-164">
              <span className="flex items-center gap-1" data-id="element-165">
                <Check className="w-4 h-4 text-green-500" data-id="element-166" /> Sin preguntas.
              </span>
              <span className="flex items-center gap-1" data-id="element-167">
                <Check className="w-4 h-4 text-green-500" data-id="element-168" /> Sin letra chica.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* SECOND CTA */}
      <section className="py-16 px-4 bg-white text-center" data-id="element-169">
        <div className="max-w-3xl mx-auto" data-id="element-170">
          <span className="inline-block bg-red-100 text-red-700 font-black px-4 py-1 rounded uppercase tracking-wider text-sm mb-4" data-id="element-171">
            DESCUENTO ESPECIAL SOLO POR HOY
          </span>
          <div className="mb-6" data-id="element-172">
            <span className="text-5xl md:text-6xl font-black text-green-600 tracking-tighter" data-id="element-173">
              USD $6,99
            </span>
            <p className="text-sm text-gray-500 mt-2" data-id="element-174">
              ↓ Pagalo en tu moneda Local ↓
            </p>
          </div>
          <CTAButton text="SÍ, QUIERO LA OFERTA AHORA" href={CHECKOUT_URL} data-id="element-175" />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-176">
        <div className="max-w-3xl mx-auto" data-id="element-177">
          <h2 className="text-3xl font-black text-center mb-4" data-id="element-178">
            Resolvé aquí tus DUDAS
          </h2>
          <p className="text-center text-gray-500 mb-10" data-id="element-179">
            Hacé clic para ver la respuesta que necesites.
          </p>
          <div className="space-y-4" data-id="element-180">
            {FAQ_DATA.map((faq, index) => <FAQItem key={index} faq={faq} index={index} isOpen={openFaq === index} onToggle={toggleFaq} data-id="element-181" />)}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 text-center text-sm" data-id="element-182">
        <div className="max-w-4xl mx-auto" data-id="element-183">
          <p className="mb-4" data-id="element-184">
            Copyright 2025 - Todos los derechos reservados.
          </p>
          <div className="flex justify-center gap-6" data-id="element-185">
            <a href="#" className="hover:text-white transition-colors" data-id="element-186">
              Privacidad
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-187">
              Términos y Condiciones
            </a>
          </div>
        </div>
      </footer>
    </div>;
}